import { cn } from '@/lib/utils';
import './spinner.css'; // Import the styles for animation

type SpinnerSize = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<SpinnerSize, { container: string; bee: string }> = {
  sm: { container: 'h-6 w-6', bee: 'h-3 w-3' },
  md: { container: 'h-12 w-12', bee: 'h-6 w-6' },
  lg: { container: 'h-16 w-16', bee: 'h-8 w-8' },
};

interface SpinnerProps {
  className?: string;
  size?: SpinnerSize;
}

/**
 * A custom spinner featuring a honeycomb hex shape and a bee icon orbiting around it.
 */
export function Spinner({ className, size = 'md' }: SpinnerProps) {
  const sizeClasses = SIZE_CLASSES[size];

  return (
    <div
      className={cn('flex items-center justify-center', className)}
      role="status"
      aria-label="Loading"
    >
      <div className={cn('relative', sizeClasses.container)}>
        {/* The Honeycomb - static shape in the honey signal color */}
        <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full text-signal">
          <polygon
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinejoin="round"
            points="50,10 84,30 84,70 50,90 16,70 16,30"
          />
        </svg>

        {/* The Bee - orbiting around honeycomb */}
        <div
          className="absolute left-1/4 top-1/4 -translate-x-1/2 -translate-y-1/2 animate-bee-orbit"
          style={{ transformOrigin: 'center' }}
        >
          {/* Bee icon - yellow body with black stripes for contrast in both themes */}
          <svg viewBox="0 0 48 48" className={sizeClasses.bee}>
            <g>
              {/* Bee body - bright yellow for visibility */}
              <path
                d="M24,4c-3.866,0-7,3.134-7,7c0,2.733,1.565,5.09,3.839,6.274C20.313,19.096,19,21.382,19,24
                c0,4.418,3.582,8,8,8s8-3.582,8-8c0-2.618-1.313-4.904-3.839-6.726C31.435,16.09,33,13.733,33,11
                C33,7.134,29.866,4,26,4H24z"
                fill="#FFCC00"
              />
              {/* Antennae - black for contrast */}
              <path
                d="M16 19c-2 0-4-2-4-4s2-4 4-4M32 19c2 0 4-2 4-4s-2-4-4-4"
                stroke="#000"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Stripes - black for contrast */}
              <path
                d="M21 29h6M20 24h8M22 19h4"
                stroke="#000"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          </svg>
        </div>
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  );
}
